import { Button } from '@commenda/kakashi';
import { useEffect, useState } from 'react';

import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { DateFilter } from '../DateFilter/DateFilter';
import { DateRangeFilterValue } from '../FilterValue/DateRangeFilterValue';
import { FilterValue } from '../FilterValue/FilterValue';
import { MultiSelectFilter } from '../MultiSelectFilter/MultiSelectFilter';
import { SingleSelectFilter } from '../SingleSelectFilter/SingleSelectFilter';
import { FilterBarProps, FilterChangeEvent, FilterConfig } from '../types';
import { getFilterValueLabel } from '../utils';

const transformExternalFilters = (
  externalFilters: Record<string, any> = {}
) => {
  const internalFilters: Record<string, string | string[]> = {
    ...externalFilters,
  };

  // Handle date transformation
  if (externalFilters.from_date || externalFilters.to_date) {
    // Always set period to custom when external dates are provided
    internalFilters.period = 'custom';
    // Remove the external date fields
    delete internalFilters.from_date;
    delete internalFilters.to_date;
  }

  // Remove corporationId if present
  delete internalFilters.corporationId;

  return internalFilters;
};

export const FilterBar = ({
  filters,
  onFilterChange,
  onFilterClear,
  onClearAll,
  filterValues,
  resetFiltersTrigger,
}: FilterBarProps) => {
  // Transform external filters to internal format
  const transformedFilters = transformExternalFilters(filterValues);

  const [activeFilters, setActiveFilters] = useState<
    Record<string, string | string[]>
  >({});

  const initialDateRange = () => {
    // Get previous month's date range
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    return {
      startDate: lastMonth,
      endDate: lastDayOfLastMonth,
    };
  };

  // Initialize with last month's date range
  const [dateRange, setDateRange] = useState(initialDateRange);

  useEffect(() => {
    if (resetFiltersTrigger) {
      setActiveFilters({});
    }
  }, [resetFiltersTrigger]);

  useEffect(() => {
    if (filterValues) {
      setActiveFilters(transformedFilters);
      if (transformedFilters.period === 'custom') {
        if (filterValues.from_date && filterValues.to_date) {
          const startDate = NonTimezoneSensitiveDate.fromISO8601String(filterValues.from_date);
          const endDate = NonTimezoneSensitiveDate.fromISO8601String(filterValues.to_date);
          setDateRange({
            startDate: startDate.toDate(),
            endDate: endDate.toDate(),
          });
        }
      }
    }
  }, [filterValues]);

  const handleCustomDateRangeChange = (startDate: Date, endDate: Date) => {
    const startDateISO =
      NonTimezoneSensitiveDate.fromNativeDate(startDate).toISO8601String();
    const endDateISO =
      NonTimezoneSensitiveDate.fromNativeDate(endDate).toISO8601String();

    // Create a custom event with date range values
    onFilterChange?.({
      filterId: 'period',
      value: [startDateISO, endDateISO],
    });
  };

  const handleFilterChange = (event: FilterChangeEvent) => {
    const { filterId, value } = event;

    setActiveFilters((prev) => {
      const newFilters = { ...prev };
      // Remove the filter key if value is an empty array, otherwise set the new value
      if (Array.isArray(value) && value.length === 0) {
        delete newFilters[filterId];
      } else {
        newFilters[filterId] = value;
      }
      return newFilters;
    });

    if (filterId === 'period' && value === 'custom') {
      // do nothing
    } else {
      onFilterChange?.(event);
    }
  };

  const handleFilterClear = (filterId: string) => {
    if (filterId === 'period') {
      setDateRange(initialDateRange());
    }
    setActiveFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterId];
      return newFilters;
    });
    onFilterClear?.(filterId);
  };

  const handleClearAll = () => {
    setActiveFilters({});
    onClearAll?.();
  };

  const renderFilter = (filter: FilterConfig) => {
    switch (filter.type) {
      case 'DATE':
        return (
          <DateFilter
            value={activeFilters[filter.id] as string}
            onChange={(value) =>
              handleFilterChange({
                filterId: filter.id,
                value,
              })
            }
            disabled={filter.disabled ?? false}
          />
        );
      case 'MULTIPLE':
        return (
          <MultiSelectFilter
            config={filter}
            values={(activeFilters[filter.id] as string[]) || []}
            onChange={(values) =>
              handleFilterChange({
                filterId: filter.id,
                value: values,
              })
            }
            disabled={filter.disabled ?? false}
          />
        );
      case 'SINGULAR':
        return (
          <SingleSelectFilter
            config={filter}
            value={activeFilters[filter.id] as string}
            onChange={(value) =>
              handleFilterChange({
                filterId: filter.id,
                value,
              })
            }
            disabled={filter.disabled ?? false}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-4 bg-bg-secondary rounded-2xl">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="txt-small-medium text-text-quaternary">Filter by</span>
        {filters.map((filter) => (
          <div key={filter.id} className="flex items-center gap-2">
            {renderFilter(filter)}
            {activeFilters[filter.id] &&
              (filter.id === 'period' &&
              activeFilters[filter.id] === 'custom' ? (
                <DateRangeFilterValue
                  label={getFilterValueLabel(filter, activeFilters[filter.id])}
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  onDateChange={(startDate, endDate) => {
                    setDateRange({ startDate, endDate });
                    handleCustomDateRangeChange(startDate, endDate);
                  }}
                  onFilterChange={handleFilterChange}
                  onRemove={() => handleFilterClear(filter.id)}
                  disabled={filter.disabled ?? false}
                />
              ) : (
                <FilterValue
                  label={getFilterValueLabel(filter, activeFilters[filter.id])}
                  onRemove={() => handleFilterClear(filter.id)}
                  disabled={filter.disabled ?? false}
                />
              ))}
          </div>
        ))}

        {Object.keys(activeFilters).length > 0 && (
          <Button
            size="sm"
            variant="tertiary"
            onClick={handleClearAll}
            className="text-text-quaternary hover:text-text-secondary txt-small-medium"
          >
            Clear all
          </Button>
        )}
      </div>
    </div>
  );
};
